import { useTranslations } from "next-intl";
import type { TimelineItemProps } from "antd/lib";
import { notification } from "antd/lib";
import { Input } from "antd/lib";
import { Avatar, Button, Empty, Skeleton, Timeline, Typography } from "antd/lib";
import React, { useMemo, useState, useEffect, useCallback } from "react";
import dayjs from "@/utils/dayjsConfig";
import { HeartFilled, HeartOutlined, RetweetOutlined, UserOutlined } from "@ant-design/icons";
import classnames from "classnames";
import axios from "axios";
import useMyMutation from "@/hooks/userMyMutation";
import { useAuth } from "@/context/AuthContext";

const CommentInput = ({ symbol, id, commentCallback }: { symbol: string; id?: string; commentCallback: () => any }) => {
  const t = useTranslations("products");

  const [text, setText] = useState("");
  const { mutate: triggerPostComment, isPending } = useMyMutation({
    url: `/v1/product/comment${id ? `/${id}` : ""}`,
    method: "post",
  });

  const onPost = useCallback(() => {
    if (!text.trim()) {
      return;
    }

    triggerPostComment({
      content: text,
      symbol,
    })
      .then(() => {
        setText("");
        commentCallback();
      })
      .catch(e =>
        notification.error({
          message: t("Error"),
          description: e?.response?.data?.message || t("Failed to post comment!"),
        }),
      );
  }, [commentCallback, symbol, t, text, triggerPostComment]);

  return (
    <div
      className={classnames("flex gap-2 items-center w-full", {
        "py-2": id,
        "pt-2 pb-6": !id,
      })}
    >
      <Avatar size={40} icon={<UserOutlined />} />
      <div className={"flex-grow"}>
        <Input
          onChange={e => setText(e.target.value)}
          value={text}
          className={"w-full"}
          placeholder={t("Write_your_comment") ?? ""}
        />
      </div>
      <Button type={"primary"} disabled={!text.trim() || isPending} onClick={onPost}>
        {t("Post")}
      </Button>
    </div>
  );
};

interface ICommentResponse {
  id: string;
  content: string;
  symbol: string;
  created_at: string;
  total_reply?: number;
  isLiked?: boolean;
  total_like?: number;
  user_id: {
    username?: string;
    email?: string;
  };
}
const limit = 10;

export const CommentTimeline = ({
  symbol,
  id,
  isLike,
  totalLike,
  totalReply,
  likeCallback,
}: {
  symbol: string;
  id?: string;
  isLike?: boolean;
  totalLike?: number;
  totalReply?: number;
  likeCallback: () => any;
}) => {
  const t = useTranslations("products");
  const { authState } = useAuth();
  const [comments, setComments] = useState<ICommentResponse[]>([]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [canLoadMore, setCanLoadMore] = useState(true);
  const apiUrl = useMemo(() => {
    let result = "";
    if (authState === "logged") {
      if (id) result = `/v1/product/comment/${id}`;
      else result = "/v1/product/comment";
    } else {
      if (id) result = `/v1/product/comment/${id}/public`;
      else result = "/v1/product/comment/public";
    }
    return result;
  }, [authState, id]);
  const fetchComments = async (isLoadMore = false) => {
    setIsLoading(true);
    try {
      const response = await axios.get(apiUrl, {
        params: {
          page: isLoadMore ? page : 1,
          symbol: symbol,
          limit: limit,
        },
      });

      const newComments = response.data.data.comments;

      if (isLoadMore) {
        setComments(prevComments => [...prevComments, ...newComments]);
        if (newComments.length < limit) {
          setCanLoadMore(false);
        } else {
          setPage(prevPage => prevPage + 1);
        }
      } else {
        setComments(newComments);
        setPage(2);
        setCanLoadMore(newComments.length >= limit);
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
    const intervalId = setInterval(() => {
      fetchComments();
    }, 15 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [symbol, id]);
  const loadMore = () => {
    if (!isLoading && canLoadMore) {
      fetchComments(true);
    }
  };
  const allComments = useMemo(() => comments, [comments]);

  const [reply, setReply] = useState(false);
  const { mutate } = useMyMutation({ url: `/v1/product/comment/${id}/like`, method: "post" });
  return (
    <Skeleton active avatar={true} loading={isLoading}>
      {id && (
        <div className="flex gap-1">
          <Button
            type={"text"}
            onClick={() => {
              mutate({}).then(() => likeCallback());
            }}
            icon={isLike ? <HeartFilled /> : <HeartOutlined />}
          >
            <Typography.Text type={"secondary"} className={"mx-1"}>
              {totalLike}
            </Typography.Text>
          </Button>
          <Button type={"text"} icon={<RetweetOutlined />}>
            <Typography.Text type={"secondary"} className={"mx-1"}>
              {totalReply}
            </Typography.Text>
          </Button>
        </div>
      )}
      {(reply || !id) && (
        <CommentInput
          symbol={symbol}
          commentCallback={() => {
            fetchComments().then();
            setReply(false);
          }}
          id={id}
        />
      )}
      {!!allComments?.length && (
        <Timeline
          className={"pt-2 px-1"}
          items={allComments.map(
            c =>
              ({
                className: "pb-0",
                dot: <Avatar size={18} icon={<UserOutlined />} />,
                key: c.id,
                children: (
                  <>
                    <div className="flex w-full justify-between">
                      <Typography.Title level={5}>{c.user_id?.username ?? c.user_id?.email}</Typography.Title>
                      <Typography.Text type={"secondary"} title={c.created_at}>
                        {dayjs(c.created_at).fromNow()}
                      </Typography.Text>
                    </div>
                    <Typography.Text type={"secondary"}>{c.content}</Typography.Text>
                    <br />
                    <CommentTimeline
                      symbol={symbol}
                      id={c.id}
                      isLike={c.isLiked}
                      totalLike={c.total_like}
                      totalReply={c.total_reply}
                      likeCallback={() => fetchComments().then()}
                    />
                  </>
                ),
              } as TimelineItemProps),
          )}
        />
      )}
      {!isLoading && canLoadMore && (
        <Button type={"link"} onClick={loadMore}>
          {t("Load_more")} {">>"}
        </Button>
      )}
      {!isLoading && !allComments?.length && !id && (
        <div className={"flex h-full justify-center items-center"}>
          <Empty description={t("Be_the_first")} />
        </div>
      )}
    </Skeleton>
  );
};
